import {Box, Button, Checkbox, FormControlLabel, Paper, Stack, Typography} from '@mui/material';
import RichEditor from './Shared/RichEditor';
import React, {useCallback, useEffect, useState} from 'react';
import PropTypes from 'prop-types';
import RichEditorResultText from './Shared/RichEditorResultText';
import {useDropzone} from 'react-dropzone';
import useNotification from '../../../../../utility/hooks/useNotification';
import UploadFileOutlinedIcon from '@mui/icons-material/UploadFileRounded';
import IconButton from '@mui/material/IconButton';
import CloseIcon from '@mui/icons-material/Close';
import InsertDriveFileRoundedIcon from '@mui/icons-material/InsertDriveFileRounded';
import RequiredCheckbox from './Shared/RequiredCheckbox';
import DownloadRoundedIcon from '@mui/icons-material/DownloadRounded';
import FormControl from '@mui/material/FormControl';
import FormHelperText from '@mui/material/FormHelperText';
import {isAllowedFile, MAX_FORM_FILE_UPLOAD_SIZE_IN_MB, rejectionMessage} from './fileUploadRules';

const SettingCheckbox = ({label, checked, disabled, testId, onChange}) => (
    <FormControlLabel
        sx={{display: 'flex', height: '40px'}}
        control={
            <Checkbox
                disabled={disabled}
                checked={checked}
                inputProps={{'data-testid': testId}}
                onChange={e => onChange(e.target.checked)}
            />
        }
        label={label}
    />
);

SettingCheckbox.propTypes = {
    label: PropTypes.string.isRequired,
    checked: PropTypes.bool,
    disabled: PropTypes.bool,
    testId: PropTypes.string,
    onChange: PropTypes.func.isRequired
};

const FileUploader = ({
                          index,
                          componentId,
                          value,
                          onChange,
                          descriptionHtml,
                          onDescriptionHtmlChange,
                          commitDescriptionHtml,
                          isEditMode,
                          enableValidation,
                          isFormArchived,
                          readOnly,
                          placeholderValues,
                          accessLevel,
                          handleDownloadAttachment,
                          error,
                          submitMode,
                          audience,
                          recipientType,
                          linkedToStudents,
                          linkedToEmployees,
                          uploadProfile,
                          saveToDocuments,
                          documentAvailableForParent,
                          documentSyncToSims,
                      }) => {

    const [draftDescriptionHtml, setDraftDescriptionHtml] = useState(descriptionHtml || '');
    const [isEditorFocused, setIsEditorFocused] = useState(false);
    const {showErrorNotification} = useNotification();
    const [uploadError, setUploadError] = useState('');

    useEffect(() => {
        if (isEditorFocused) return;
        setDraftDescriptionHtml(descriptionHtml || '');
    }, [descriptionHtml, isEditorFocused]);

    const handleDraftChange = useCallback(
        (nextDescriptionHtml) => {
            setDraftDescriptionHtml(nextDescriptionHtml);
            onDescriptionHtmlChange(nextDescriptionHtml);
        },
        [onDescriptionHtmlChange]
    );

    const handleFocus = useCallback(() => {
        setIsEditorFocused(true);
    }, []);

    const handleBlur = useCallback(() => {
        setIsEditorFocused(false);
        commitDescriptionHtml();
    }, [commitDescriptionHtml]);

    const onDrop = useCallback((acceptedFiles) => {
        if (acceptedFiles.length) {
            setUploadError('');
            onChange({
                id: componentId,
                value: acceptedFiles[0]
            });
        }
    }, [componentId, onChange]);

    // The message shows beside the button as well, because a form opened from a link has no
    // notification bar to show it in.
    const onDropRejected = useCallback(
        (rejections) => {
            const message = rejectionMessage(rejections);
            setUploadError(message);
            showErrorNotification(message);
        },
        [showErrorNotification],
    );

    // The plain file picker, on every device. The newer picker Chrome offers on Android handed
    // files back that never arrived on the form (AD-2360). Files are checked by type or by name,
    // so a photo with no type from a phone's gallery is still taken.
    const {getInputProps, open} = useDropzone({
        onDrop,
        onDropRejected,
        onError: () => setUploadError('The file could not be opened. Try choosing it again.'),
        noClick: true,
        noKeyboard: true,
        useFsAccessApi: false,
        validator: file => (isAllowedFile(file)
            ? null
            : {code: 'file-invalid-type', message: 'That type of file cannot be attached.'}),
        multiple: false,
        maxSize: MAX_FORM_FILE_UPLOAD_SIZE_IN_MB * 1024 * 1024,
    });

    return (
        <Box display="flex" flexDirection="column" gap={2} sx={{my: 2}}>
            {isEditMode
                ? (
                    <RichEditor
                        currentDescription={draftDescriptionHtml}
                        setCurrentDescription={handleDraftChange}
                        isFormLive={isFormArchived}
                        accessLevel={accessLevel}
                        audience={audience}
                        recipientType={recipientType}
                        linkedToStudents={linkedToStudents}
                        linkedToEmployees={linkedToEmployees}
                        placeholderValues={placeholderValues}
                        onFocus={handleFocus}
                        onBlur={handleBlur}
                    />
                )
                : (
                    <RichEditorResultText
                        id={componentId}
                        description={descriptionHtml}
                        placeholderValues={placeholderValues}
                        readOnly={readOnly}
                        enableValidation={enableValidation}
                        audience={audience}
                        recipientType={recipientType}
                        linkedToStudents={linkedToStudents}
                        linkedToEmployees={linkedToEmployees}
                        submitMode={submitMode}
                    />
                )}

            {value
                ? (
                    <Paper variant="outlined" sx={{width: 'fit-content'}}>
                        <Stack
                            flexDirection="row"
                            justifyContent="space-between"
                            alignItems="center"
                            paddingTop={0.5}
                            paddingBottom={0.5}
                            paddingLeft={1}
                            paddingRight={1}
                            gap={1}
                        >
                            <InsertDriveFileRoundedIcon
                                fontSize="small"
                                sx={{color: 'primary.main'}}
                            />
                            <Typography variant="body2">{value.name}</Typography>
                            {!readOnly
                                ? (
                                    <IconButton
                                        size="small"
                                        sx={{padding: 0.25}}
                                        onClick={() => onChange({
                                            id: componentId,
                                            value: null
                                        })}
                                    >
                                        <CloseIcon fontSize="small"/>
                                    </IconButton>
                                )
                                : value?.fileKey && handleDownloadAttachment
                                    ? (
                                        <IconButton
                                            size="small"
                                            onClick={() => handleDownloadAttachment(value?.fileKey)}
                                        >
                                            <DownloadRoundedIcon fontSize="small"/>
                                        </IconButton>
                                    )
                                    : null}
                        </Stack>
                    </Paper>
                )
                : !readOnly
                    ? (
                        <FormControl error={!!error} variant="standard">
                            <Stack flexDirection="row" alignItems="center" gap={1}>
                                <input {...getInputProps()} />
                                <Button
                                    onClick={open}
                                    disabled={!submitMode}
                                    startIcon={<UploadFileOutlinedIcon/>}
                                    variant="outlined"
                                >
                                    Add File
                                </Button>
                                <Typography
                                    variant="body2"
                                    color={!submitMode ? 'text.disabled' : 'text.secondary'}
                                >
                                    Max file size {MAX_FORM_FILE_UPLOAD_SIZE_IN_MB} MB
                                </Typography>
                            </Stack>
                            <FormHelperText>{uploadError || error}</FormHelperText>
                        </FormControl>
                    )
                    : null}

            {isEditMode && (
                <RequiredCheckbox
                    enableValidation={enableValidation}
                    index={index}
                    onChange={onChange}
                    isFormArchived={isFormArchived}
                />
            )}

            {/* AD-2788 (Maria, 26 October 2026): the uploaded file is also saved to the Documents tab of the student's,
                applicant's or member of staff's profile when the form is submitted, and stays with the response */}
            {isEditMode && uploadProfile && (
                <Box sx={{mt: -2}}>
                    <SettingCheckbox
                        label={`Save the uploaded file to the Documents tab of the ${uploadProfile === 'staff' ? 'staff' : 'student'} profile`}
                        checked={!!saveToDocuments}
                        disabled={isFormArchived}
                        testId="file-uploader-save-to-documents"
                        onChange={checked => onChange({index, saveToDocuments: checked})}
                    />
                    {/* AD-2789: a file saved to a member of staff's Documents tab can also go to their SIMS staff
                        record. Staff documents are never shown to parents, so only the child's form asks that. */}
                    {saveToDocuments && (
                        <Box sx={{pl: 4, mt: -1}}>
                            {uploadProfile === 'child' && (
                                <SettingCheckbox
                                    label="Available for parent"
                                    checked={!!documentAvailableForParent}
                                    disabled={isFormArchived}
                                    testId="file-uploader-available-for-parent"
                                    onChange={checked => onChange({index, documentAvailableForParent: checked})}
                                />
                            )}
                            <SettingCheckbox
                                label="Sync to SIMS Linked Documents"
                                checked={!!documentSyncToSims}
                                disabled={isFormArchived}
                                testId="file-uploader-sync-to-sims"
                                onChange={checked => onChange({index, documentSyncToSims: checked})}
                            />
                        </Box>
                    )}
                </Box>
            )}
        </Box>
    );
};

FileUploader.propTypes = {
    componentId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    value: PropTypes.any,
    index: PropTypes.number,
    descriptionHtml: PropTypes.string,
    onDescriptionHtmlChange: PropTypes.func,
    commitDescriptionHtml: PropTypes.func,
    enableValidation: PropTypes.bool,
    onChange: PropTypes.func,
    isEditMode: PropTypes.bool,
    isFormArchived: PropTypes.bool,
    readOnly: PropTypes.bool,
    placeholderValues: PropTypes.object,
    accessLevel: PropTypes.number,
    handleDownloadAttachment: PropTypes.func,
    error: PropTypes.string,
    submitMode: PropTypes.bool,
    audience: PropTypes.number,
    recipientType: PropTypes.string,
    linkedToStudents: PropTypes.bool,
    linkedToEmployees: PropTypes.bool,
    uploadProfile: PropTypes.oneOf(['child', 'staff']),
    saveToDocuments: PropTypes.bool,
    documentAvailableForParent: PropTypes.bool,
    documentSyncToSims: PropTypes.bool,
};

export default FileUploader;
